/**
 * Trial variant tracking utilities for the free trial length experiment
 *
 * This file handles reading the `trial_variant` cookie that is set by the
 * WP/landing marketing site (cross-subdomain, `.artfulagenda.com`) and, as a
 * fallback for ads that link directly into the app, capturing the variant
 * from a `?trial=` URL parameter instead.
 *
 * The captured variant is read by the app to decide which trial length
 * messaging/behavior to show, and mirrors the backend's default of
 * `control` when no variant can be determined.
 */

/* eslint-env browser */

const TRIAL_VARIANT_COOKIE_NAME = 'trial_variant';
const TRIAL_VARIANT_COOKIE_DOMAIN = '.artfulagenda.com';
const TRIAL_VARIANT_COOKIE_MAX_AGE = 2592000; // ~30 days

const VALID_TRIAL_VARIANTS = ['five_day', 'control'];

// Maps the `?trial=` URL parameter values used by ad landing pages to the
// cookie values understood by the app/backend.
const URL_TRIAL_PARAM_MAP = {
  '5day': 'five_day',
  '14day': 'control',
};

/**
 * Reads the trial variant value from the "trial_variant" cookie
 * Returns the variant value if found and valid, null if not found, invalid, or on error
 */
export const getTrialVariantCookie = () => {
  try {
    // Check if document.cookie is available (browser environment)
    if (typeof document === 'undefined' || !document.cookie) {
      console.log('Trial Variant: No cookies available');
      return null;
    }

    // Parse document.cookie to find the "trial_variant" cookie
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();

      // Check if this cookie starts with "trial_variant="
      if (cookie.indexOf('trial_variant=') === 0) {
        const variantValue = cookie.substring('trial_variant='.length); // Remove prefix

        if (variantValue) {
          if (VALID_TRIAL_VARIANTS.indexOf(variantValue) !== -1) {
            console.log('Trial Variant: Found valid trial_variant in cookie:', variantValue);
            return variantValue;
          } else {
            console.warn(
              'Trial Variant: Invalid trial_variant value in cookie, ignoring:',
              variantValue
            );
            return null;
          }
        }
      }
    }

    console.log('Trial Variant: trial_variant cookie not found');
    return null;
  } catch (error) {
    console.error('Trial Variant: Error reading trial_variant from cookie:', error);
    return null;
  }
};

/**
 * Captures the trial variant from a "trial" URL parameter for ads pointed
 * directly at the app (no landing page cookie set yet).
 * FIRST-TOUCH: only writes the cookie if one doesn't already exist, so we
 * never overwrite the variant assigned by the landing page.
 * Returns true if the trial_variant cookie was written from the URL.
 */
export const captureTrialVariantFromUrl = () => {
  // implemented for easier testing purposes
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const trialParam = urlParams.get('trial');

    if (!trialParam) {
      return false;
    }

    const variant = URL_TRIAL_PARAM_MAP[trialParam];

    if (!variant) {
      console.warn('Trial Variant: Unknown trial parameter in URL, ignoring:', trialParam);
      return false;
    }

    // First-touch: don't overwrite an existing trial_variant cookie
    if (getTrialVariantCookie()) {
      console.log('Trial Variant: trial_variant cookie already set, not overwriting from URL');
      return false;
    }

    console.log('Trial Variant: Found trial parameter in URL:', trialParam, '->', variant);

    document.cookie = `${TRIAL_VARIANT_COOKIE_NAME}=${variant}; domain=${TRIAL_VARIANT_COOKIE_DOMAIN}; path=/; max-age=${TRIAL_VARIANT_COOKIE_MAX_AGE}; SameSite=Lax; Secure`;

    const newUrl = new URL(window.location);
    newUrl.searchParams.delete('trial');
    window.history.replaceState({}, '', newUrl.toString());
    console.log('Trial Variant: Cleaned URL after capturing trial variant');

    return true;
  } catch (error) {
    console.error('Trial Variant: Error capturing trial variant from URL:', error);
    return false;
  }
};

/**
 * Gets the trial variant, falling back to "control" when missing or invalid
 * to match the backend's default behavior.
 */
export const getTrialVariant = () => {
  const variant = getTrialVariantCookie();

  if (VALID_TRIAL_VARIANTS.indexOf(variant) !== -1) {
    return variant;
  }

  return 'control';
};

/**
 * Boot entry point for capturing the trial variant.
 * The cookie is the primary channel, set by the landing page snippet, so
 * this only needs to handle the direct-to-app URL fallback.
 * Returns true if a trial_variant cookie was written from the URL.
 */
export const captureTrialVariant = () => captureTrialVariantFromUrl();
